import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listKnowledgeBases,
  listKnowledgeBasesPaginated,
} from "../service/apiService";
import { useUser } from "../context/UserContext";

export interface BrainBankOption {
  id: string;
  name: string;
  index_name?: string;
  file_count?: number;
}

/**
 * The BrainBanks the current user can put a file into.
 *
 * Admins see every BrainBank; everyone else sees their own plus the ones
 * assigned to them, merged and de-duplicated. Extracted from BrainBankSelector
 * so the meetings export dialog cannot drift from the list the rest of the app
 * shows.
 */
export function useBrainBanks(enabled = true) {
  const { userId, userDetails } = useUser();
  const isAdmin = userDetails?.role !== "user";

  const [brainBanks, setBrainBanks] = useState<BrainBankOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolves to the list it loaded (undefined when it could not load one), so a
  // caller that needs one BrainBank out of it -- reopening a conversation -- can
  // wait for it instead of reading the list state before it has arrived.
  const fetch = useCallback(async (): Promise<BrainBankOption[] | undefined> => {
    if (!userId) return undefined;

    setLoading(true);
    setError(null);
    try {
      let result: any[];

      if (isAdmin) {
        result = (await listKnowledgeBases()) ?? [];
      } else {
        const [mine, assigned] = await Promise.all([
          listKnowledgeBasesPaginated(1, 100, "my_knowledge_base"),
          listKnowledgeBasesPaginated(1, 100, "assigned_knowledge_base"),
        ]);

        // A BrainBank can be both owned and assigned; keyed by id so it is
        // offered once.
        const byId = new Map<string, any>();
        [
          ...(mine?.knowledge_bases ?? []),
          ...(assigned?.knowledge_bases ?? []),
        ].forEach((kb: any) => byId.set(kb.id, kb));
        result = Array.from(byId.values());
      }

      const loaded: BrainBankOption[] = result.map((kb: any) => ({
        id: kb.id,
        name: kb.knowledgebase_name || kb.name,
        index_name: kb.index_name,
        // Counted server-side from the file rows. The `file_ids` attribute on
        // the BrainBank row is not a count -- nothing has appended to it since
        // the processor lambda's summary update was disabled, so it reads 0 for
        // BrainBanks that do hold files. Left undefined when the server could
        // not count, so the row shows no number rather than a wrong one.
        file_count: typeof kb.file_count === "number" ? kb.file_count : undefined,
      }));
      setBrainBanks(loaded);
      return loaded;
    } catch (err) {
      console.error("Error loading BrainBanks:", err);
      setError("Couldn't load your BrainBanks.");
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [userId, isAdmin]);

  useEffect(() => {
    if (enabled) {
      fetch();
    }
  }, [enabled, fetch]);

  const sorted = useMemo(
    () =>
      [...brainBanks].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      ),
    [brainBanks]
  );

  return { brainBanks: sorted, loading, error, refresh: fetch };
}

export default useBrainBanks;
